import { Table } from 'react-bootstrap';
import { getJobsPerformance } from '../../../services/jobs';
import { onQuerySuccess } from '../../../utils/queryResponse';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import JobsPerformanceFilter from './jobsPerformanceFilter';
import { msToHHMM } from '../../../utils/timeHelpers';
import { toLocalDateString } from '../../../utils/helper';
import Skeleton, { SkeletonRows } from '../../../components/skeleton';
import SetTempEditOrderModal from '../../../components/modals/orderEditTemp';

const EyeIcon = () => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
);

const pageStyles = `
  .sbr .periodbar{display:flex;align-items:center;gap:8px;margin-bottom:18px;flex-wrap:wrap}
  .sbr .periodbar .since{margin-left:auto;font-size:12.5px;color:var(--muted);font-weight:600;display:flex;align-items:center;gap:7px}
  .sbr .periodbar .since .ic{width:14px;height:14px;color:var(--faint)}
  .sbr .kpi .cap{display:flex;align-items:center;gap:6px;font-size:11.5px;font-weight:700;margin-top:6px}
  .sbr .kpi .cap.up{color:var(--good)} .sbr .kpi .cap.down{color:var(--bad)} .sbr .kpi .cap.flat{color:var(--muted)}
  .sbr .kpi .cap .ic{width:12px;height:12px}
  .sbr .kpi .sub2{font-size:11.5px;color:var(--faint);font-weight:600;margin-top:4px}
  .sbr .kpi .ico{position:absolute;top:14px;right:14px;width:30px;height:30px;border-radius:9px;background:var(--brand-50);
    border:1px solid var(--brand-100);display:flex;align-items:center;justify-content:center;color:var(--brand)}
  .sbr .kpi .ico .ic{width:15px;height:15px}
  .sbr .kpi.lift .ico{background:rgba(255,255,255,.12);border-color:rgba(255,255,255,.22);color:#fff}
  .sbr .kpi.lift .cap.up{color:#6EE7B7} .sbr .kpi.lift .sub2{color:rgba(255,255,255,.6)}
  .sbr .rank{display:flex;align-items:center;gap:12px;padding:12px 0;border-bottom:1px solid var(--line-soft)}
  .sbr .rank:last-child{border-bottom:0}
  .sbr .rank .pos{width:22px;height:22px;border-radius:7px;background:var(--surface);color:var(--muted);font-size:11px;
    font-weight:800;display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .sbr .rank:first-of-type .pos{background:var(--brand-50);color:var(--brand-ink);border:1px solid var(--brand-100)}
  .sbr .rank .bd{flex:1;min-width:0}
  .sbr .rank .bd .hl{display:flex;align-items:baseline;gap:8px;justify-content:space-between;margin-bottom:6px}
  .sbr .rank .bd .nm{font-size:13px;font-weight:700;color:var(--ink);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .sbr .rank .bd .vv{font-size:13.5px;font-weight:800;color:var(--ink);font-variant-numeric:tabular-nums;white-space:nowrap;flex-shrink:0}
  .sbr .rank .bd .vv .pct{font-size:11px;color:var(--muted);font-weight:700;margin-left:5px}
  .sbr .rank .track{height:7px}
  .sbr .totgrid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:14px}
  .sbr .totcell{background:var(--surface);border:1px solid var(--line);border-radius:var(--r-md);padding:13px 14px}
  .sbr .totcell .l{font-size:11.5px;color:var(--muted);font-weight:600}
  .sbr .totcell .n{font-size:18px;font-weight:800;letter-spacing:-.5px;color:var(--ink);margin-top:6px;font-variant-numeric:tabular-nums}
  .sbr .totline{display:flex;align-items:center;justify-content:space-between;padding:10px 0;border-bottom:1px solid var(--line-soft)}
  .sbr .totline:last-child{border-bottom:0}
  .sbr .totline .l{font-size:13px;color:var(--muted);font-weight:600}
  .sbr .totline .v{font-size:13.5px;font-weight:800;color:var(--ink);font-variant-numeric:tabular-nums}
`;

const JobsPerformancePage = () => {

    const [editJobModal, setEditJobModal] = useState(false)
    const { t } = useTranslation();
    const [jobPerformanceData, setJobPerformanceData] = useState()
    const [selectedJobId, setSelectedJobId] = useState('')
    const [filters, setFilters] = useState(['', '', ''])

    const { data, isFetching } = useQuery(
        ['jobs-performance', ...filters],
        () => getJobsPerformance(...filters)
    )

    useEffect(() => { onQuerySuccess(data, setJobPerformanceData) }, [data])

    const handleEditJob = (job) => { setSelectedJobId(job.id); setEditJobModal(true) }

    const tableRows = (items) => items?.map((x, i) => (
        <tr key={i}>
            <td>
                <button title={t('reports.view')} style={{ background: 'none', border: 'none', padding: '2px', cursor: 'pointer', color: 'var(--brand)' }}
                    onClick={() => handleEditJob(x.job)}>
                    <EyeIcon />
                </button>
            </td>
            <td><span className="num" style={{ fontWeight: 700, color: 'var(--brand-ink)' }}>#{x.job.id}</span></td>
            <td style={{ fontWeight: 600, color: 'var(--ink)' }}>{x.job.client.firstName} {x.job.client.lastName}</td>
            <td className="num">{msToHHMM(x.actualDuration)}</td>
            <td style={{ color: 'var(--muted)', fontWeight: 500 }}>{toLocalDateString(x.job.completionTimeActual)}</td>
        </tr>
    ))

    return (
        <div className="sbr">
            <style>{pageStyles}</style>
            <SetTempEditOrderModal display={editJobModal} toggle={setEditJobModal} jobId={selectedJobId} />

            {/* Desktop header */}
            <div className="dhead sbr-d">
                <div>
                    <div className="ttl">{t('reports.jobsPerformanceTitle')}</div>
                    <div className="sub">{t('reports.performanceSubtitle')}</div>
                </div>
                <div className="act">
                    <button className="btn-sbr btn-ghost-sbr btn-sm-sbr">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        {t('reports.export')}
                    </button>
                </div>
            </div>

            {/* Mobile header */}
            <div className="sbr-m" style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.8px', color: 'var(--ink)' }}>{t('reports.performance')}</div>
            </div>

            {/* Period filter */}
            <div style={{ marginBottom: 14 }}>
                <JobsPerformanceFilter onFilter={setFilters} showStatus />
            </div>

            {/* KPIs */}
            <div className="kpis4" style={{ marginBottom: 18 }}>
                <div className="kpi" style={{ position: 'relative' }}>
                    <div className="ico">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    </div>
                    <div className="l">{t('reports.onTimeRate')}</div>
                    {isFetching && !jobPerformanceData
                        ? <Skeleton width="64%" height={26} style={{ margin: '8px 0 2px' }} />
                        : <div className="n num">{Math.round(jobPerformanceData?.completedOnTimePercentage) || 0}<span style={{ fontSize: 16, color: 'var(--muted)' }}>%</span></div>}
                    <div className="sub2">{t('reports.completionOnTimeRate')}</div>
                </div>

                <div className="kpi" style={{ position: 'relative' }}>
                    <div className="ico">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    </div>
                    <div className="l">{t('reports.minDuration')}</div>
                    {isFetching && !jobPerformanceData
                        ? <Skeleton width="64%" height={26} style={{ margin: '8px 0 2px' }} />
                        : <div className="n num">{msToHHMM(jobPerformanceData?.minDuration) || '—'}</div>}
                    <div className="sub2">{t('reports.shortestCompletedJob')}</div>
                </div>

                <div className="kpi" style={{ position: 'relative' }}>
                    <div className="ico">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 20V10M12 20V4M6 20v-6"/></svg>
                    </div>
                    <div className="l">{t('reports.avgDuration')}</div>
                    {isFetching && !jobPerformanceData
                        ? <Skeleton width="64%" height={26} style={{ margin: '8px 0 2px' }} />
                        : <div className="n num">{msToHHMM(jobPerformanceData?.avgDuration) || '—'}</div>}
                    <div className="sub2">{t('reports.averageJobDuration')}</div>
                </div>

                <div className="kpi lift" style={{ position: 'relative' }}>
                    <div className="ico">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
                    </div>
                    <div className="l">{t('reports.maxDuration')}</div>
                    {isFetching && !jobPerformanceData
                        ? <Skeleton width="64%" height={26} style={{ margin: '8px 0 2px' }} />
                        : <div className="n num">{msToHHMM(jobPerformanceData?.maxDuration) || '—'}</div>}
                    <div className="sub2">{t('reports.longestCompletedJob')}</div>
                </div>
            </div>

            {/* Main cols */}
            <div className="cols">
                {/* Left: ranked tables */}
                <div>
                    {[
                        { title: t('reports.top5LongestJobs'), items: jobPerformanceData?.topMoreTimeDuration },
                        { title: t('reports.top5ShortestJobs'), items: jobPerformanceData?.topLessTimeDuration },
                    ].map(({ title, items }) => (
                        <div key={title} className="card-sbr" style={{ marginBottom: 18 }}>
                            <div className="ch">
                                <svg className="ci" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                <span className="t">{title}</span>
                            </div>
                            {isFetching && !items?.length ? (
                                <SkeletonRows rows={5} widths={['8%', '16%', '42%', '18%', '16%']} />
                            ) : !items?.length ? (
                                <div className="sbr-empty">
                                    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                    {t('reports.noDataForPeriod')}
                                </div>
                            ) : (
                                <>
                                    {items.map((x, i) => {
                                        const maxDur = items[0]?.actualDuration || 1
                                        const pct = Math.round((x.actualDuration / maxDur) * 100)
                                        return (
                                            <div key={i} className="rank">
                                                <div className="pos">{i + 1}</div>
                                                <div className="bd">
                                                    <div className="hl">
                                                        <div className="nm" style={{ cursor: 'pointer' }} onClick={() => handleEditJob(x.job)}>
                                                            #{x.job.id} · {x.job.client?.firstName} {x.job.client?.lastName}
                                                        </div>
                                                        <div className="vv">{msToHHMM(x.actualDuration)}</div>
                                                    </div>
                                                    <div className="track"><i style={{ width: `${pct}%` }}></i></div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </>
                            )}
                        </div>
                    ))}
                </div>

                {/* Right rail: totals */}
                <div>
                    <div className="card-sbr" style={{ margin: 0 }}>
                        <div className="ch">
                            <svg className="ci" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                            <span className="t">{t('reports.totals')}</span>
                            <span className="more">{t('reports.thisPeriod')}</span>
                        </div>
                        <div className="totgrid">
                            <div className="totcell">
                                <div className="l">{t('reports.onTimeRate')}</div>
                                <div className="n">{Math.round(jobPerformanceData?.completedOnTimePercentage) || 0}%</div>
                            </div>
                            <div className="totcell">
                                <div className="l">{t('reports.avgDuration')}</div>
                                <div className="n">{msToHHMM(jobPerformanceData?.avgDuration) || '—'}</div>
                            </div>
                        </div>
                        <div className="totline"><span className="l">{t('reports.longestJob')}</span><span className="v num">{msToHHMM(jobPerformanceData?.maxDuration) || '—'}</span></div>
                        <div className="totline"><span className="l">{t('reports.shortestJob')}</span><span className="v num">{msToHHMM(jobPerformanceData?.minDuration) || '—'}</span></div>
                        <div className="totline"><span className="l">{t('reports.top5LongestAvg')}</span><span className="v num">
                            {jobPerformanceData?.topMoreTimeDuration?.length
                                ? msToHHMM(jobPerformanceData.topMoreTimeDuration.reduce((s, x) => s + x.actualDuration, 0) / jobPerformanceData.topMoreTimeDuration.length)
                                : '—'}
                        </span></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default JobsPerformancePage;
